import moment from "moment-timezone";
import { DAYS_OF_WEEK } from "./constants";

export function inclusiveDateToStart(dateStr, timezoneName) {
  return moment.tz(dateStr, "YYYY-MM-DD", timezoneName || "UTC").startOf("day").toISOString();
}

export function inclusiveDateToExclusiveEnd(dateStr, timezoneName) {
  return moment
    .tz(dateStr, "YYYY-MM-DD", timezoneName || "UTC")
    .add(1, "day")
    .startOf("day")
    .toISOString();
}

export function exclusiveEndToInclusiveDate(iso, timezoneName) {
  if (!iso) return "";
  return moment.tz(iso, timezoneName || "UTC").subtract(1, "millisecond").format("YYYY-MM-DD");
}

export function instantToLocalDate(iso, timezoneName) {
  if (!iso) return "";
  return moment.tz(iso, timezoneName || "UTC").format("YYYY-MM-DD");
}

export function instantToLocalTime(iso, timezoneName) {
  if (!iso) return "";
  return moment.tz(iso, timezoneName || "UTC").format("HH:mm");
}

export function localDateTimeToIso(dateStr, timeStr, timezoneName) {
  return moment
    .tz(`${dateStr} ${timeStr || "00:00"}`, "YYYY-MM-DD HH:mm", timezoneName || "UTC")
    .toISOString();
}

export function localTimeCandidates(date, time, timezoneName) {
  const wallTime = `${date} ${time}`;
  const parsed = moment.utc(wallTime, "YYYY-MM-DD HH:mm", true);
  const zone = moment.tz.zone(timezoneName);
  if (!parsed.isValid() || !zone) return [];
  return [...new Set(zone.offsets)].map((offset) => parsed.valueOf() + offset * 60000)
    .filter((instant) => moment.tz(instant, timezoneName).format("YYYY-MM-DD HH:mm:ss") === `${wallTime}:00`)
    .sort((a, b) => a - b).map((instant) => new Date(instant).toISOString());
}

export function zonedRangeToInstants(values, timezoneName) {
  const resolve = (date, time, preferred, label) => {
    const candidates = localTimeCandidates(date, time, timezoneName);
    if (!candidates.length) throw new Error(`${label} is invalid or skipped by daylight saving. Choose another time.`);
    const preserved = preferred && candidates.find((candidate) => moment(candidate).isSame(preferred));
    if (preserved) return preserved;
    if (candidates.length > 1) throw new Error(`Choose which ${label.toLowerCase()} to use; the clock repeats this time.`);
    return candidates[0];
  };
  const startsAt = resolve(values.localDate, values.startTime, values.startInstant, "Start time");
  const endsAt = resolve(values.endDate || values.localDate, values.endTime, values.endInstant, "End time");
  if (moment(endsAt).isSameOrBefore(startsAt)) throw new Error("End must be after start. Check the end date for overnight hours.");
  return { startsAt, endsAt };
}

export function instantRangeToLocal(startsAt, endsAt, timezoneName) {
  return {
    localDate: instantToLocalDate(startsAt, timezoneName),
    endDate: instantToLocalDate(endsAt, timezoneName),
    startTime: instantToLocalTime(startsAt, timezoneName),
    endTime: instantToLocalTime(endsAt, timezoneName),
    startInstant: startsAt,
    endInstant: endsAt,
  };
}

export function formatInZone(iso, timezoneName, pattern = "YYYY-MM-DD HH:mm") {
  if (!iso) return "";
  return moment.tz(iso, timezoneName || "UTC").format(pattern);
}

export function formatRange(startsAt, endsAt, timezoneName) {
  const sameDay = instantToLocalDate(startsAt, timezoneName) === instantToLocalDate(endsAt, timezoneName);
  return `${formatInZone(startsAt, timezoneName, "MMM D, YYYY h:mm A")} – ${formatInZone(
    endsAt,
    timezoneName,
    sameDay ? "h:mm A" : "MMM D, YYYY h:mm A"
  )}`;
}

export function weekdayFromLocalDate(dateStr) {
  const isoWeekday = moment.utc(dateStr, "YYYY-MM-DD").isoWeekday();
  return DAYS_OF_WEEK[isoWeekday - 1]?.value;
}

export function daysBetween(fromDate, toDate) {
  return moment.utc(toDate, "YYYY-MM-DD").diff(moment.utc(fromDate, "YYYY-MM-DD"), "days");
}

export function cycleWeekIndex(cycleAnchorDate, localDate, cycleWeeks) {
  const weeks = Number(cycleWeeks) || 1;
  const delta = daysBetween(cycleAnchorDate, localDate);
  return ((Math.floor(delta / 7) % weeks) + weeks) % weeks;
}

export function timeToMinutes(time) {
  const [hours, minutes] = String(time || "00:00")
    .slice(0, 5)
    .split(":")
    .map(Number);
  return hours * 60 + minutes;
}

export function isOvernightWindow(startTime, endTime) {
  return timeToMinutes(endTime) <= timeToMinutes(startTime);
}

function categoryKey(ids) {
  return [...(ids || [])].slice().sort().join(",");
}

function shiftGroupKey(window) {
  return [
    Number(window.weekIndex || 0),
    window.dayOfWeek,
    window.mode || "regular",
    categoryKey(window.serviceCategoryIds),
  ].join("|");
}

const DAY_ORDER = Object.fromEntries(DAYS_OF_WEEK.map((day, index) => [day.value, index]));

function compareWindows(left, right) {
  return (
    Number(left.weekIndex || 0) - Number(right.weekIndex || 0) ||
    (DAY_ORDER[left.dayOfWeek] ?? 0) - (DAY_ORDER[right.dayOfWeek] ?? 0) ||
    (left.mode || "").localeCompare(right.mode || "") ||
    categoryKey(left.serviceCategoryIds).localeCompare(categoryKey(right.serviceCategoryIds)) ||
    String(left.startTime || "").localeCompare(String(right.startTime || ""))
  );
}

function toFormWindow(window, breaks = []) {
  return {
    weekIndex: Number(window.weekIndex || 0),
    dayOfWeek: window.dayOfWeek,
    startTime: window.startTime,
    endTime: window.endTime,
    mode: window.mode || "regular",
    serviceCategoryIds: window.serviceCategoryIds || [],
    breaks,
  };
}

/** Merge same-day bookable windows into one shift plus lunch/break gaps. */
export function collapseWindowsToShifts(windows = []) {
  const sorted = [...windows].sort(compareWindows);
  const groups = [];
  sorted.forEach((window) => {
    const last = groups[groups.length - 1];
    if (last && shiftGroupKey(last[0]) === shiftGroupKey(window)) {
      last.push(window);
    } else {
      groups.push([window]);
    }
  });

  const shifts = groups.flatMap((group) => {
    if (group.some((window) => isOvernightWindow(window.startTime, window.endTime))) {
      return group.map((window) => toFormWindow(window));
    }
    const ordered = [...group].sort((left, right) =>
      String(left.startTime).localeCompare(String(right.startTime))
    );
    const overlaps = ordered.some(
      (window, index) =>
        index > 0 && timeToMinutes(window.startTime) < timeToMinutes(ordered[index - 1].endTime)
    );
    if (overlaps) {
      return group.map((window) => toFormWindow(window));
    }
    const breaks = [];
    for (let index = 1; index < ordered.length; index += 1) {
      if (timeToMinutes(ordered[index].startTime) > timeToMinutes(ordered[index - 1].endTime)) {
        breaks.push({
          startTime: ordered[index - 1].endTime,
          endTime: ordered[index].startTime,
        });
      }
    }
    return [
      toFormWindow(
        {
          ...ordered[0],
          startTime: ordered[0].startTime,
          endTime: ordered[ordered.length - 1].endTime,
        },
        breaks
      ),
    ];
  });

  return shifts.sort((left, right) =>
    left.weekIndex - right.weekIndex ||
    (DAY_ORDER[left.dayOfWeek] ?? 0) - (DAY_ORDER[right.dayOfWeek] ?? 0) ||
    timeToMinutes(left.startTime) - timeToMinutes(right.startTime)
  );
}

/** Validate lunch/break times against a same-day shift. Regular hours punch these as gaps; extra hours and date changes save them as blocks. */
export function normalizeShiftBreaks(shift) {
  const breaks = (shift.breaks || []).filter((item) => item.startTime && item.endTime);
  if (!breaks.length) {
    return { error: null, breaks: [] };
  }
  if (isOvernightWindow(shift.startTime, shift.endTime)) {
    return { error: "Lunch and breaks can only be added when the day ends after it starts." };
  }

  const shiftStart = timeToMinutes(shift.startTime);
  const shiftEnd = timeToMinutes(shift.endTime);
  const normalized = breaks
    .map((item) => ({
      startTime: item.startTime,
      endTime: item.endTime,
      start: timeToMinutes(item.startTime),
      end: timeToMinutes(item.endTime),
    }))
    .sort((left, right) => left.start - right.start);

  for (const item of normalized) {
    if (item.end <= item.start) {
      return { error: "Each lunch or break must end after it starts." };
    }
    if (item.start < shiftStart || item.end > shiftEnd) {
      return { error: "Lunch and breaks must fall inside that day's start and end times." };
    }
  }
  for (let index = 1; index < normalized.length; index += 1) {
    if (normalized[index].start < normalized[index - 1].end) {
      return { error: "Lunch and breaks on the same day cannot overlap." };
    }
  }
  return { error: null, breaks: normalized.map(({ startTime, endTime }) => ({ startTime, endTime })) };
}

/** Turn same-day breaks into calendar-block instants. Hours stay intact. */
export function shiftBreaksToBlockRanges(shift, localDate, timezoneName) {
  const endDate = shift.endDate || localDate;
  if ((shift.breaks || []).some((item) => item.startTime && item.endTime) && endDate !== localDate) {
    return { error: "Lunch and breaks can only be added when the day ends after it starts.", ranges: [] };
  }
  const validated = normalizeShiftBreaks(shift);
  if (validated.error) {
    return { error: validated.error, ranges: [] };
  }
  try {
    return {
      error: null,
      ranges: validated.breaks.map((pause) =>
        zonedRangeToInstants(
          {
            localDate,
            endDate: localDate,
            startTime: pause.startTime,
            endTime: pause.endTime,
          },
          timezoneName
        )
      ),
    };
  } catch (error) {
    return { error: error.message, ranges: [] };
  }
}

/** Punch lunch/break gaps out of a shift so the API still receives bookable windows. */
export function expandShiftsToWindows(shifts = []) {
  const windows = [];
  for (const shift of shifts) {
    const result = expandShift(shift);
    if (result.error) {
      return { error: result.error, windows: [] };
    }
    windows.push(...result.windows);
  }
  return { error: null, windows };
}

function expandShift(shift) {
  const base = {
    weekIndex: Number(shift.weekIndex || 0),
    dayOfWeek: shift.dayOfWeek,
    mode: shift.mode || "regular",
    serviceCategoryIds: shift.serviceCategoryIds,
  };
  const validated = normalizeShiftBreaks(shift);
  if (validated.error) {
    return { error: validated.error };
  }
  if (!validated.breaks.length) {
    return {
      windows: [{ ...base, startTime: shift.startTime, endTime: shift.endTime }],
    };
  }

  const shiftStart = timeToMinutes(shift.startTime);
  const shiftEnd = timeToMinutes(shift.endTime);
  const segments = [];
  let cursor = shiftStart;
  let cursorTime = shift.startTime;
  validated.breaks.forEach((item) => {
    const start = timeToMinutes(item.startTime);
    if (start > cursor) {
      segments.push({ startTime: cursorTime, endTime: item.startTime });
    }
    cursor = timeToMinutes(item.endTime);
    cursorTime = item.endTime;
  });
  if (cursor < shiftEnd) {
    segments.push({ startTime: cursorTime, endTime: shift.endTime });
  }
  if (!segments.length) {
    return { error: "A day needs bookable hours after lunch and breaks." };
  }
  return { windows: segments.map((segment) => ({ ...base, ...segment })) };
}

export function previewRuleEvents({
  weeklyWindows = [],
  cycleWeeks = 1,
  cycleAnchorDate,
  weekStart,
  rangeStart,
  rangeEnd,
  effectiveFrom,
  effectiveTo,
  timezoneName,
}) {
  const start = rangeStart
    ? moment.tz(rangeStart, timezoneName || "UTC").startOf("day")
    : moment.tz(weekStart, timezoneName || "UTC").startOf("isoWeek");
  const end = rangeEnd
    ? moment.tz(rangeEnd, timezoneName || "UTC").startOf("day")
    : start.clone().add(7, "days");
  const events = [];
  for (let day = start.clone().subtract(1, "day"); day.isBefore(end); day = day.clone().add(1, "day")) {
    const localDate = day.format("YYYY-MM-DD");
    if (effectiveFrom && localDate < effectiveFrom) continue;
    if (effectiveTo && localDate > effectiveTo) continue;
    const weekIndex = cycleWeekIndex(cycleAnchorDate || localDate, localDate, cycleWeeks);
    const dayOfWeek = weekdayFromLocalDate(localDate);
    weeklyWindows
      .filter((window) => Number(window.weekIndex || 0) === weekIndex && window.dayOfWeek === dayOfWeek)
      .forEach((window, idx) => {
        const startAt = moment.tz(`${localDate} ${window.startTime}`, "YYYY-MM-DD HH:mm", timezoneName || "UTC");
        let endAt = moment.tz(`${localDate} ${window.endTime}`, "YYYY-MM-DD HH:mm", timezoneName || "UTC");
        if (!endAt.isAfter(startAt)) {
          const nextDate = day.clone().add(1, "day").format("YYYY-MM-DD");
          endAt = moment.tz(`${nextDate} ${window.endTime}`, "YYYY-MM-DD HH:mm", timezoneName || "UTC");
        }
        if (!endAt.isAfter(start) || !startAt.isBefore(end)) return;
        events.push({
          id: `preview-${localDate}-${idx}`,
          title: `${window.startTime}–${window.endTime}`,
          start: startAt.toISOString(),
          end: endAt.toISOString(),
        });
      });
  }
  return events;
}
